import React, { useState, useEffect, useMemo } from 'react';
import { FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Modal from 'components/Tvendo/Common/ModalBase';
import TextField from '@material-ui/core/TextField';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import MultiComplete from 'components/Tvendo/Common/MultiComplete';
import { wsGetAll } from 'services/WService';

export default function ModalDiscountRules(props) {
  const discountRules = props.model;
  const [dataChanging, setDataChanging] = useState(discountRules);

  const [businessPartners, setBusinessPartners] = useState([]);
  const [marks, setMarks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lines, setLines] = useState([]);
  const [families, setFamilies] = useState([]);
  const [bureaus, setBureaus] = useState([]);
  const [zones, setZones] = useState([]);

  const [selectedZones, setSelectedZones] = useState([]);
  const [selectedBureaus, setSelectedBureaus] = useState([]);

  const warehouseZones = useMemo(
    () => zones.filter((z) => z.code && z.defCashier && z.code === z.defCashier),
    [zones],
  );

  // Cada zona de almacén incluye las zonas que atiende (Chincha: Cañete y Pisco):
  // se muestran sus mesas y la regla se guarda con todas esas zonas, que son las de los pedidos
  const selectedZoneIds = useMemo(() => {
    const warehouseCodes = selectedZones.map((z) => z.code);
    return zones.filter((z) => warehouseCodes.includes(z.defCashier)).map((z) => String(z.id));
  }, [zones, selectedZones]);
  // los efectos usan el texto para no correr cada vez que se vuelven a pedir las zonas
  const selectedZoneKey = selectedZoneIds.join(',');

  // Las mesas dependen de las zonas seleccionadas. Sin zona elegida -> ninguna.
  const filteredBureaus = useMemo(() => {
    if (selectedZoneIds.length === 0) return [];
    return bureaus.filter((b) => selectedZoneIds.includes(String(b.base_zone_id)));
  }, [bureaus, selectedZoneIds]);

  async function listBusinessPartners() {
    let response = await wsGetAll('base/business-partner?CardType=S&QryGroup1=Y');
    if (response.success) setBusinessPartners(response.data);
  }

  // Filtros en cadena: socio -> marca y categoría, categoría -> línea, línea -> familia
  async function listMarks(businessPartnerId) {
    let response = await wsGetAll(`logi/mark?business_partners[]=${businessPartnerId}`);
    if (response.success) setMarks(response.data);
  }

  async function listCategories(businessPartnerId) {
    let response = await wsGetAll(`logi/category?base_business_partner_id=${businessPartnerId}`);
    if (response.success) setCategories(response.data);
  }

  // sin categoría elegida van todas las líneas del socio
  async function listLines(businessPartnerId, categoryId) {
    let url = !!categoryId
      ? `logi/line?logi_category_id=${categoryId}`
      : `logi/line?business_partners[]=${businessPartnerId}`;
    let response = await wsGetAll(url);
    if (response.success) setLines(response.data);
  }

  async function listFamilies(lineIds) {
    if (lineIds.length === 0) {
      setFamilies([]);
      return;
    }
    let response = await wsGetAll('logi/family?' + lineIds.map((id) => `lines[]=${id}`).join('&'));
    if (response.success) setFamilies(response.data);
  }

  async function listBureaus() {
    let response = await wsGetAll('base/bureau');
    if (response.success) setBureaus(response.data);
  }

  async function listZones() {
    let response = await wsGetAll('base/zone');
    if (response.success) setZones(response.data);
  }

  function handleChange(field, value) {
    setDataChanging({
      ...dataChanging,
      [field]: value,
    });
  }

  // Cambia un campo y limpia los que dependen de él
  function handleChanges(values) {
    setDataChanging({
      ...dataChanging,
      ...values,
    });
  }

  useEffect(() => {
    if (discountRules.open) {
      listBusinessPartners();
      listBureaus();
      listZones();
      if (!discountRules.id) {
        setSelectedZones([]);
        setSelectedBureaus([]);
      }
    }
  }, [discountRules.open]);

  useEffect(() => {
    handleChange('base_zone_ids', selectedZoneIds.map(Number));
  }, [selectedZoneKey]);

  useEffect(() => {
    handleChange('base_bureau_ids', selectedBureaus.map((b) => b.id));
  }, [selectedBureaus]);

  useEffect(() => {
    setSelectedBureaus((prev) =>
      prev.filter((b) => selectedZoneIds.includes(String(b.base_zone_id))),
    );
  }, [selectedZoneKey]);

  // Al editar se cargan el socio y los filtros guardados en la regla;
  // las listas de marca, categoría, línea y familia se piden solas con estos valores
  useEffect(() => {
    if (businessPartners.length > 0 && discountRules.base_business_partner_id) {
      handleChanges({
        base_business_partner_id: String(discountRules.base_business_partner_id),
        logi_mark_id: discountRules.logi_mark_id ?? null,
        logi_category_id: discountRules.logi_category_id ?? null,
        logi_line_id: discountRules.logi_line_id ?? null,
        logi_family_id: discountRules.logi_family_id ?? null,
      });
    }
  }, [businessPartners]);

  useEffect(() => {
    if (warehouseZones.length > 0 && Array.isArray(discountRules.base_zone_ids)) {
      const ids = discountRules.base_zone_ids.map(String);
      setSelectedZones(warehouseZones.filter((z) => ids.includes(String(z.id))));
    }
  }, [warehouseZones]);

  useEffect(() => {
    if (bureaus.length > 0 && Array.isArray(discountRules.base_bureau_ids)) {
      const ids = discountRules.base_bureau_ids.map(String);
      setSelectedBureaus(bureaus.filter((b) => ids.includes(String(b.id))));
    }
  }, [bureaus]);

  useEffect(() => {
    const businessPartnerId = dataChanging.base_business_partner_id;
    if (businessPartnerId === null || businessPartnerId === undefined) {
      setMarks([]);
      setCategories([]);
      setLines([]);
      return;
    }
    listMarks(businessPartnerId);
    listCategories(businessPartnerId);
  }, [dataChanging.base_business_partner_id]);

  useEffect(() => {
    const businessPartnerId = dataChanging.base_business_partner_id;
    if (businessPartnerId === null || businessPartnerId === undefined) return;
    listLines(businessPartnerId, dataChanging.logi_category_id);
  }, [dataChanging.base_business_partner_id, dataChanging.logi_category_id]);

  // sin línea elegida van las familias de todas las líneas de la lista
  useEffect(() => {
    listFamilies(!!dataChanging.logi_line_id ? [dataChanging.logi_line_id] : lines.map((l) => l.id));
  }, [lines, dataChanging.logi_line_id]);

  return (
    <Modal
      title="Reglas de descuento"
      model={discountRules}
      setOpen={props.setOpen}
      showNotify={props.showNotify}
      reload={props.reload}
      dataChanging={dataChanging}
      setDataChanging={setDataChanging}
      entity="logi/discount-rule"
      fullWidth
      maxWidth="md"
      state
    >
      <GridContainer>
        <GridItem xs={12} md={6}>
          <Autocomplete
            disableClearable
            options={businessPartners}
            getOptionLabel={(item) => `${item.CardName}`}
            renderInput={(params) => (
              <TextField {...params} label="Socio de Negocio" margin="normal" fullWidth />
            )}
            onChange={(e, item) =>
              handleChanges({
                base_business_partner_id: item?.id,
                logi_mark_id: null,
                logi_category_id: null,
                logi_line_id: null,
                logi_family_id: null,
              })
            }
            value={
              businessPartners.find(
                (b) => String(b.id) === String(dataChanging.base_business_partner_id),
              ) || null
            }
          />
        </GridItem>

        <GridItem xs={12} md={6}>
          <Autocomplete
            options={marks}
            getOptionLabel={(item) => item.description}
            renderInput={(params) => <TextField {...params} label="Marca" margin="normal" fullWidth />}
            onChange={(e, item) => handleChange('logi_mark_id', item?.id ?? null)}
            value={marks.find((m) => String(m.id) === String(dataChanging.logi_mark_id)) || null}
          />
        </GridItem>

        <GridItem xs={12} md={6}>
          <Autocomplete
            options={categories}
            getOptionLabel={(item) => item.description}
            renderInput={(params) => (
              <TextField {...params} label="Categoría" margin="normal" fullWidth />
            )}
            onChange={(e, item) =>
              handleChanges({
                logi_category_id: item?.id ?? null,
                logi_line_id: null,
                logi_family_id: null,
              })
            }
            value={
              categories.find((c) => String(c.id) === String(dataChanging.logi_category_id)) || null
            }
          />
        </GridItem>

        <GridItem xs={12} md={6}>
          <Autocomplete
            options={lines}
            getOptionLabel={(item) => item.description}
            renderInput={(params) => <TextField {...params} label="Línea" margin="normal" fullWidth />}
            onChange={(e, item) =>
              handleChanges({
                logi_line_id: item?.id ?? null,
                logi_family_id: null,
              })
            }
            value={lines.find((l) => String(l.id) === String(dataChanging.logi_line_id)) || null}
          />
        </GridItem>

        <GridItem xs={12} md={6}>
          <Autocomplete
            options={families}
            getOptionLabel={(item) => item.description}
            renderInput={(params) => (
              <TextField {...params} label="Familia" margin="normal" fullWidth />
            )}
            onChange={(e, item) => handleChange('logi_family_id', item?.id ?? null)}
            value={families.find((f) => String(f.id) === String(dataChanging.logi_family_id)) || null}
          />
        </GridItem>

        <GridItem xs={12} md={6}>
          <TextField
            label="Cantidad mínima (cajas)"
            name="quantity_min"
            margin="normal"
            type="number"
            fullWidth
            defaultValue={!!discountRules ? discountRules.quantity_min : ''}
            inputProps={{ min: 0, step: 1 }}
            required
          />
        </GridItem>

        <GridItem xs={12} md={6}>
          <MultiComplete
            label="Zonas"
            selectAll
            limitTags={3}
            textField="description"
            options={warehouseZones}
            selected={selectedZones}
            setSelected={setSelectedZones}
          />
        </GridItem>

        <GridItem xs={12} md={6}>
          <MultiComplete
            label="Mesas"
            selectAll
            limitTags={3}
            textField="description"
            options={filteredBureaus}
            selected={selectedBureaus}
            setSelected={setSelectedBureaus}
            disabled={selectedZones.length === 0}
          />
        </GridItem>

        <GridItem xs={12} md={6}>
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>Funcionalidad de detalle</InputLabel>
            <Select defaultValue={discountRules.detail_type || 'E'} inputProps={{ name: 'detail_type' }}>
              <MenuItem value="E">Excluir</MenuItem>
              <MenuItem value="I">Incluir</MenuItem>
            </Select>
          </FormControl>
        </GridItem>

        <GridItem xs={12} md={6}>
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>Tipo detalle</InputLabel>
            <Select defaultValue={discountRules.item_type || 'P'} disabled>
              <MenuItem value="C">Categoría</MenuItem>
              <MenuItem value="L">Línea</MenuItem>
              <MenuItem value="F">Família</MenuItem>
              <MenuItem value="P">Producto</MenuItem>
            </Select>
          </FormControl>
        </GridItem>
      </GridContainer>
    </Modal>
  );
}
